import { Controller } from "@hotwired/stimulus"
import { CostCalculator } from "~/helpers/costCalculator"
import { LeadsSettingsHelper } from "~/helpers/leads_settings_helper"
import { TempFormHelper } from "~/helpers/temp_form_helper"

export default class extends Controller {
  static get targets() {
    return [
      "result", "bulkActionsToolbar", "revealEmailsCount", "revealCreditsCount", "selectedCount", "revealCost",
      "exportCost", "exportDialogCost", "totalCount", "sectionCheckbox", "dropdownTrigger",
      "leadsListSelectList", "newListName", "newListNameError", "listType", "loadMoreButton",
      "lackOfCreditsEmailsCount", "lackOfCreditsCurrentCredits", "lackOfCreditsCost", "lackOfCreditsCopy",
      "lackOfCreditsProposedEmailsCount"
    ]
  }

  static get values() {
    return {
      singleCreditsBucket: { type: Boolean, default: false },
      exportUrl: { type: String, default: null },
      currentNumberOfCredits: { type: Number, default: 0 },
      domain: { type: String, default: null }
    }
  }

  connect() {
    this.selectedEmailIds = []
    this.selectedDepartments = []
    this.leadsListSelectize = $(this.leadsListSelectListTarget).selectize()
    this.leadsListSelectize[0].selectize.on("change", () => {
      this.updateLeadsListSettings()
    })
  }

  disconnect() {
    if (this.leadsListSelectize !== undefined) {
      this.leadsListSelectize[0].selectize.destroy()
    }
  }

  selectEmail(event) {
    let result = this.resultTargets.find(x => x.dataset.emailId == event.currentTarget.dataset.emailId)
    result.classList.toggle("selected")

    const emailId = Number(event.currentTarget.dataset.emailId)
    const selected = result.classList.contains("selected")

    if (selected) {
      this.selectedEmailIds.push(emailId)
    } else {
      this.selectedEmailIds = this.selectedEmailIds.filter(id => id !== emailId)
    }

    this.#updateUiAfterSelection()
  }

  selectDepartmentEmails(event) {
    const checked = event.currentTarget.checked
    const department = event.params.section
    const resultsInDepartment = this.resultTargets.filter(x => x.parentElement.id == department)
    const loadMoreButton = this.loadMoreButtonTargets.find(x => event.params.sectionId === x.dataset.sectionId)

    if (checked) {
      resultsInDepartment.forEach((result) => {
        result.classList.add("selected")
        this.selectedEmailIds.push(Number(result.dataset.emailId))
      })
    } else {
      resultsInDepartment.forEach((result) => {
        result.classList.remove("selected")
        this.selectedEmailIds = this.selectedEmailIds.filter(id => id !== Number(result.dataset.emailId))
      })
    }
    this.selectedEmailIds = [...new Set(this.selectedEmailIds)]

    if (loadMoreButton) {
      const loadMoreLink = loadMoreButton.querySelector("a")

      this.#triggerLoadMore(loadMoreLink.href, () => {
        const resultsInDepartment = this.resultTargets.filter(x => x.parentElement.id == department)

        resultsInDepartment.forEach((result) => {
          result.classList.add("selected")
          this.selectedEmailIds.push(Number(result.dataset.emailId))
        })

        this.selectedEmailIds = [...new Set(this.selectedEmailIds)]
        this.#updateUiAfterSelection()
      })
    }

    this.#updateUiAfterSelection()
  }

  selectAll() {
    this.resultTargets.forEach((result) => {
      result.classList.add("selected")
      this.selectedEmailIds.push(Number(result.dataset.emailId))
    })

    this.sectionCheckboxTargets.forEach((sectionCheckbox) => {
      sectionCheckbox.checked = true
    })

    this.selectedEmailIds = [...new Set(this.selectedEmailIds)]
    this.#updateUiAfterSelection()

    // We have to use fetch below because normal visits don't work due to the fact that browser
    // cancels multiple requests from links.
    this.loadMoreButtonTargets.forEach((loadMoreButton) => {
      const loadMoreLink = loadMoreButton.querySelector("a")

      this.#triggerLoadMore(loadMoreLink.href, () => {
        this.resultTargets.forEach((result) => {
          result.classList.add("selected")
          this.selectedEmailIds.push(Number(result.dataset.emailId))
        })

        this.selectedEmailIds = [...new Set(this.selectedEmailIds)]
        this.#updateUiAfterSelection()
      })
    })
  }

  unselectAll() {
    this.resultTargets.forEach((result) => {
      result.classList.remove("selected")
      result.querySelector("input[type='checkbox']").checked = false
    })

    this.sectionCheckboxTargets.forEach((sectionCheckbox) => {
      sectionCheckbox.checked = false
    })

    this.selectedEmailIds = []
    this.#updateUiAfterSelection()
  }

  dismissLeadsListsDropdown() {
    this.dropdownTriggerTarget.click()
  }

  revealAndSaveLeads() {
    const selectedListType = this.listTypeTargets.find(x => x.checked)

    if (selectedListType.value === "existing") {
      this.#revealEmailAddresses(this.leadsListSelectListTarget.value)
    } else {
      const newListName = this.newListNameTarget.value

      if (newListName.trim().length === 0) {
        this.newListNameErrorTarget.classList.remove("d-none")
        this.newListNameTarget.focus()
        return
      }

      fetch(App.api.createLeadList(), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ "name": newListName })
      }).then(response => {
        if (!response.ok) { throw new Error(response.status) }

        return response.json()
      }).then(response => {
        const newListId = response.data.id
        this.dispatch("listCreated", { detail: { list_id: response.data.id } })
        this.newListNameTarget.value = ""
        this.newListNameErrorTarget.classList.add("d-none")
        this.#revealEmailAddresses(newListId)
      })
    }
  }

  listTypeSelection() {
    const checkedOption = this.listTypeTargets.find(x => x.checked)

    if (checkedOption.value === "existing") {
      this.leadsListSelectize[0].selectize.enable()
      this.newListNameTarget.disabled = true
      this.newListNameTarget.required = false
      this.newListNameErrorTarget.classList.add("d-none")
    } else if (checkedOption.value === "new") {
      this.leadsListSelectize[0].selectize.disable()
      this.newListNameTarget.disabled = false
      this.newListNameTarget.required = true
    }
  }

  refreshLeadsLists() {
    fetch(App.api.leadsList(100))
      .then(response => {
        if (!response.ok) { throw new Error(response.status) }

        return response.json()
      }).then(response => {
        // Destroy selectize
        if (this.leadsListSelectize) {
          this.leadsListSelectize[0].selectize.destroy()
          delete this.leadsListSelectize
        }

        this.leadsListSelectListTarget.innerHTML = ""
        response.data.leads_lists.forEach(list => {
          const option = document.createElement("option")
          option.setAttribute("value", list.id)
          option.setAttribute("data", JSON.stringify(list))
          option.textContent = list.name
          this.leadsListSelectListTarget.appendChild(option)
        })

        // And recreate it once the list has been refreshed
        this.leadsListSelectize = $(this.leadsListSelectListTarget).selectize()
        this.leadsListSelectize[0].selectize.on("change", () => {
          this.updateLeadsListSettings()
        })
      }).catch(error => {
        if (error.message === "500") {
          App.displayFlash("danger", i18n.t("js.front.api_errors.internal_server_error"))
        }
      })
  }

  updateLeadsListSettings() {
    const leadsListId = this.leadsListSelectListTarget.value
    LeadsSettingsHelper.updateLeadsListSettings(leadsListId)
  }

  deselectEmail(event) {
    const emailId = Number(event.currentTarget.dataset.emailId)
    this.selectedEmailIds = this.selectedEmailIds.filter(id => id !== emailId)

    this.#updateRevealCost()
    this.#toggleBulkActionsToolbar()
  }

  exportEmails() {
    if (this.currentNumberOfCreditsValue === 0) {
      App.displayFlash("danger", i18n.t("js.front.domain_search.export.lack_of_credits"))
      return
    }

    // Reveal unrevealed emails
    const dialog = document.getElementById("export-confirmation-dialog")
    dialog.showModal()

    const emailIdsToExport = this.selectedEmailIds

    dialog.addEventListener("close", () => {
      if (dialog.returnValue === "confirm") {
        this.#handleExport(emailIdsToExport)
        this.unselectAll()
      }
    }, { once: true })
  }

  #handleExport(emailIds) {
    const formData = new FormData()
    formData.append("email_ids", emailIds.join(","))

    fetch(this.exportUrlValue, {
      method: "POST",
      body: formData,
      headers: {
        "X-CSRF-Token": document.querySelector("[name='csrf-token']").content
      }
    }).then(response => {
      const contentType = response.headers.get("content-type")

      if (contentType && contentType.includes("application/json")) {
        // Handle JSON response
        return response.json().then(data => {
          if (data.message) {
            App.displayFlash("success", data.message)
          }
        })
      } else if (contentType && contentType.includes("text/csv")) {
        // Handle CSV file download
        return response.blob().then(blob => {
          const url = window.URL.createObjectURL(blob)
          const a = document.createElement("a")
          a.href = url
          a.download = this.#extractFilename(response.headers.get("content-disposition")) || "export.csv"
          document.body.appendChild(a)
          a.click()
          window.URL.revokeObjectURL(url)
          document.body.removeChild(a)
        })
      }
    }).catch(() => {
      App.displayFlash("danger", i18n.t("js.front.api_errors.internal_server_error"))
    })
  }

  #extractFilename(contentDisposition) {
    if (!contentDisposition) return null

    // Try to extract filename from filename="..." or filename*=UTF-8''...
    const filenameMatch = contentDisposition.match(/filename[*]?=['"]?([^'";]+)['"]?/i)
    if (filenameMatch) {
      // Handle UTF-8 encoded filenames (filename*=UTF-8''...)
      if (filenameMatch[0].includes('*=')) {
        return decodeURIComponent(filenameMatch[1])
      }
      // Handle regular quoted filenames
      return filenameMatch[1].replace(/['"]/g, '')
    }

    return null
  }

  #revealEmailAddresses(selectedLeadsListId) {
    if (!selectedLeadsListId) {
      return
    }

    const form = TempFormHelper.create(`/search/${this.domainValue}/reveal`, "post", (form) => {
      TempFormHelper.addHiddenInput(form, "email_ids", this.selectedEmailIds.join(","))
      TempFormHelper.addHiddenInput(form, "leads_list_id", selectedLeadsListId)
      TempFormHelper.addHiddenInput(form, "save_as_lead", true)
    })

    form.addEventListener("submit", () => {
      this.unselectAll()
    })

    form.requestSubmit()
  }

  #getEmailIdsByRevealedStatus(isRevealed) {
    if (this.selectedEmailIds.length === 0) {
      return 0
    }

    return [...new Set(this.resultTargets.map((result) => {
      if (this.selectedEmailIds.includes(Number(result.dataset.emailId)) && result.dataset.revealed === isRevealed.toString()) {
        return Number(result.dataset.emailId)
      } else {
        return null
      }
    }).filter(Boolean))]
  }

  #updateRevealCost() {
    const unrevealedEmailIds = this.#getEmailIdsByRevealedStatus(false)
    const costToReveal = CostCalculator.calculate("search", unrevealedEmailIds.length, this.singleCreditsBucketValue)
    const costToRevealVisual = CostCalculator.calculateVisualRepresentation("search", unrevealedEmailIds.length, this.singleCreditsBucketValue)
    const visualCurrentCredits = this.singleCreditsBucketValue ? this.currentNumberOfCreditsValue / CostCalculator.UNIFIED_CREDITS_BASE : this.currentNumberOfCreditsValue
    this.revealCostTarget.innerHTML = this.revealCostTarget.innerHTML.replace(/\d+/, costToRevealVisual)

    if (this.hasExportCostTarget) {
      this.exportCostTarget.innerHTML = this.exportCostTarget.innerHTML.replace(/\d+/, costToRevealVisual)

      const dialogCostText = [
        costToRevealVisual,
        this.singleCreditsBucketValue ? "" : "search",
        "credits"
      ].filter(Boolean).join(" ")

      this.exportDialogCostTarget.textContent = dialogCostText

      if (costToReveal > this.currentNumberOfCreditsValue) {
        this.#updateLackOfCreditsExportModalCostText(unrevealedEmailIds, visualCurrentCredits)
      } else {
        this.lackOfCreditsCopyTarget.classList.add("d-none")
      }
    }
  }

  #updateLackOfCreditsExportModalCostText(unrevealedEmailIds, visualCurrentCredits) {
    const numberOfMailsToReveal = Math.floor(this.currentNumberOfCreditsValue / CostCalculator.calculate("search", 1, this.singleCreditsBucketValue))
    this.lackOfCreditsCopyTarget.classList.remove("d-none")
    this.lackOfCreditsEmailsCountTargets.forEach(target => {
      target.textContent = unrevealedEmailIds.length
    })
    this.lackOfCreditsCurrentCreditsTargets.forEach(target => {
      target.textContent = visualCurrentCredits
    })
    this.lackOfCreditsProposedEmailsCountTarget.textContent = numberOfMailsToReveal

    const actualCostVisual = numberOfMailsToReveal > 0
      ? CostCalculator.calculateVisualRepresentation("search", numberOfMailsToReveal, this.singleCreditsBucketValue)
      : 0

    const currentCreditsText = [
      actualCostVisual,
      this.singleCreditsBucketValue ? "" : "search",
      "credits"
    ].filter(Boolean).join(" ")

    this.lackOfCreditsCostTargets.forEach(target => {
      target.textContent = currentCreditsText
    })
  }

  #toggleBulkActionsToolbar() {
    if (this.selectedEmailIds.length > 0) {
      this.bulkActionsToolbarTarget.classList.add("is-visible")
    } else {
      this.bulkActionsToolbarTarget.classList.remove("is-visible")
    }
  }

  #updateUiAfterSelection() {
    this.#updateRevealCost()
    this.#toggleBulkActionsToolbar()

    // Make sure that same email ID is checked everywhere
    this.resultTargets.forEach((result) => {
      if (this.selectedEmailIds.includes(Number(result.dataset.emailId))) {
        result.querySelector("input[type='checkbox']").checked = true
      } else {
        result.querySelector("input[type='checkbox']").checked = false
      }
    })

    this.selectedCountTarget.textContent = this.selectedEmailIds.length
  }

  #triggerLoadMore(href, callback) {
    fetch(href, {
      headers: {
        "Turbo-Frame": "true",
        "Accept": "text/vnd.turbo-stream.html"
      }
    }).then(response => {
      return response.text()
    }).then(html => {
      Turbo.renderStreamMessage(html)

      return Promise.resolve()
    }).then(() => {
      setTimeout(callback, 25) // Just so it will go at end of event loop
    })
  }
}
